import React, {useEffect, useRef, useState} from 'react'
import styled from 'styled-components'
import {getCurrentColors} from '../utils/colors'

const COLORS = getCurrentColors() // Importando as cores do tema atual

const CarouselWrapper = styled.div`
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
`

const Viewport = styled.div`
    width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
`

const CarouselTrack = styled.div<{$translateX: number; $isDragging: boolean}>`
    display: flex;
    width: 100%;
    height: 100%;
    transform: ${({$translateX}) => `translate3d(${$translateX}%, 0, 0)`};
    transition: ${({$isDragging}) =>
        $isDragging ? 'none' : 'transform 450ms ease'};
    will-change: transform;
`

const Slide = styled.div`
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    min-width: 0;
    min-height: 0;
`

const CarouselImage = styled.img`
    display: block;
    flex: 0 0 100%;
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 0;
    user-select: none;
    -webkit-user-drag: none;
`

const NavigationButton = styled.button<{$direction: 'prev' | 'next'}>`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    background-color: rgba(17, 24, 39, 0.45);
    border: none;
    color: white;
    width: 36px;
    height: 36px;
    border-radius: 50%;
    cursor: pointer;
    z-index: 2;
    ${({$direction}) =>
        $direction === 'prev' ? 'left: 12px;' : 'right: 12px;'}
    display: flex;
    align-items: center;
    justify-content: center;
    backdrop-filter: blur(2px);

    &:hover {
        background-color: rgba(17, 24, 39, 0.7);
    }

    @media (max-width: 1200px) {
        width: 32px;
        height: 32px;
    }
`

const DotContainer = styled.div`
    position: absolute;
    left: 0;
    right: 0;
    bottom: 14px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 6px;
    z-index: 2;
`

const Dot = styled.button<{$active: boolean}>`
    width: 8px;
    height: 10px;
    border-radius: 5px;
    background-color: ${({$active}) => ($active ? '#FFFFFF' : '#FFFFFF80')};
    border: none;
    cursor: pointer;
    transition:
        width 0.2s ease,
        background-color 0.2s ease;
    width: ${({$active}) => ($active ? '18px' : '8px')};
    z-index: 2;

    &:hover {
        background-color: ${COLORS.primariaEscura};
    }
`

interface CarouselProps {
    images: string[]
}

const AUTOPLAY_INTERVAL = 8000
const SWIPE_THRESHOLD = 40

const Carousel: React.FC<CarouselProps> = ({images}) => {
    const [currentIndex, setCurrentIndex] = useState(0)
    const [isDragging, setIsDragging] = useState(false)
    const [isPaused, setIsPaused] = useState(false)
    const touchStartX = useRef<number | null>(null)

    const hasManyImages = images.length > 1

    const handleNext = () => {
        setCurrentIndex((prevIndex) => (prevIndex + 1) % images.length)
    }

    const handlePrev = () => {
        setCurrentIndex((prevIndex) =>
            prevIndex === 0 ? images.length - 1 : prevIndex - 1
        )
    }

    const goToSlide = (index: number) => {
        setCurrentIndex(index)
    }

    const onTouchStart: React.TouchEventHandler<HTMLDivElement> = (event) => {
        if (!hasManyImages) {
            return
        }

        touchStartX.current = event.touches[0]?.clientX ?? null
        setIsDragging(true)
    }

    const onTouchEnd: React.TouchEventHandler<HTMLDivElement> = (event) => {
        if (!hasManyImages) {
            return
        }

        const startX = touchStartX.current
        const endX = event.changedTouches[0]?.clientX

        setIsDragging(false)
        touchStartX.current = null

        if (startX === null || typeof endX !== 'number') {
            return
        }

        const delta = endX - startX
        if (Math.abs(delta) < SWIPE_THRESHOLD) {
            return
        }

        if (delta < 0) {
            handleNext()
            return
        }

        handlePrev()
    }

    useEffect(() => {
        if (currentIndex >= images.length && images.length > 0) {
            setCurrentIndex(0)
        }
    }, [images.length, currentIndex])

    useEffect(() => {
        if (!hasManyImages || isPaused) {
            return
        }

        const interval = setInterval(handleNext, AUTOPLAY_INTERVAL)

        return () => clearInterval(interval)
    }, [hasManyImages, isPaused, images.length])

    if (!images.length) {
        return null
    }

    return (
        <CarouselWrapper
            onMouseEnter={() => setIsPaused(true)}
            onMouseLeave={() => setIsPaused(false)}
            onFocus={() => setIsPaused(true)}
            onBlur={() => setIsPaused(false)}
            onTouchStart={() => setIsPaused(true)}
            onTouchEnd={() => setIsPaused(false)}
        >
            <Viewport onTouchStart={onTouchStart} onTouchEnd={onTouchEnd}>
                <CarouselTrack
                    $translateX={-currentIndex * 100}
                    $isDragging={isDragging}
                >
                    {images.map((src, index) => (
                        <Slide key={index}>
                            <CarouselImage
                                src={src}
                                alt={`Slide ${index + 1}`}
                                loading={index === 0 ? 'eager' : 'lazy'}
                                draggable={false}
                            />
                        </Slide>
                    ))}
                </CarouselTrack>

                {hasManyImages && (
                    <>
                        <NavigationButton
                            $direction="prev"
                            onClick={handlePrev}
                            aria-label="Slide anterior"
                            type="button"
                        >
                            &#8249;
                        </NavigationButton>
                        <NavigationButton
                            $direction="next"
                            onClick={handleNext}
                            aria-label="Próximo slide"
                            type="button"
                        >
                            &#8250;
                        </NavigationButton>
                    </>
                )}

                {hasManyImages && (
                    <DotContainer>
                        {images.map((_, index) => (
                            <Dot
                                key={index}
                                $active={index === currentIndex}
                                onClick={() => goToSlide(index)}
                                aria-label={`Ir para slide ${index + 1}`}
                                type="button"
                            />
                        ))}
                    </DotContainer>
                )}
            </Viewport>
        </CarouselWrapper>
    )
}

export default Carousel
